import { forwardRef, Fragment, useCallback, useEffect, useRef } from 'react';
import { CloseTab, DisclosureNext, MenuCheck } from '@sas/react-svg';
import cx from 'classnames';
import { EarconName, useEarcon } from '../../../hooks/useEarcon';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../../hooks/useKeyboardTooltip';
import { isTruncated, useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { getOSName, OSName, restoreTextSelection, saveTextSelection } from '../../../util';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Icon } from '../../Icon';
import { InvisibleText } from '../../InvisibleText';
import { GlobalSettingsKeys } from '../../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../../Settings/GlobalSettingsProvider';
import { MenuPopover } from './MenuPopover';
import bundle from '../NovaComponents-Menu-gui-icu.properties';
import './MenuListItem.css';
import cn from './MenuListItem.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlIds = createUseHtmlId('MenuListItem');
/**
 * @internal
 */
export const MenuListItem = /*#__PURE__*/forwardRef(function MenuListItem(props, forwardedRef) {
  const itemRef = useRef(null);
  const liRef = useRef(null);
  const isKeyDownRef = useRef(false);
  const isRTL = useIsRTL();
  const localize = useTextLocalizer();
  const truncationMainTextRef = useTruncationTooltip();
  const truncationMainTextHtmlRef = useRef(null);
  const truncationSecondaryTextRef = useTruncationTooltip();
  const truncationSecondaryTextHtmlRef = useRef(null);
  const dt = useDesignTokens();
  const earcon = useEarcon({
    source: EarconName.Error
  });

  // various element ids that will be pointed to by ariaLabelled/Describedby
  const [customIconMarkerId, indentationMarkerId, textMarkerId, additionalAriaTextMarkerId, deleteInfoMarkerId, infoTextId] = useHtmlIds(6);
  const {
    index,
    sectionIndex,
    onSelected,
    onClosed,
    onItemHovered,
    onItemLeaved,
    showFocus,
    item
  } = props;
  const getTooltip = () => {
    const basicTooltips = [];
    const fullTooltips = [];
    if (item.icon && 'tooltip' in item.icon && item.icon.tooltip) basicTooltips.push(item.icon.tooltip);
    if (basicTooltips.length ||
    // indicates that item.icon.tooltip exists.
    truncationMainTextHtmlRef.current && isTruncated(truncationMainTextHtmlRef.current) || truncationSecondaryTextHtmlRef.current && isTruncated(truncationSecondaryTextHtmlRef.current)) {
      if (item.text) basicTooltips.push(item.text);
      if (item.info?.infoText) basicTooltips.push(item.info.infoText);
    }
    if (basicTooltips.length) fullTooltips.push(basicTooltips.join(', '));
    if (item.tooltip) fullTooltips.push(item.tooltip);
    if (item.closable && !item.subSections?.length && !props.disabled) fullTooltips.push(localize('item.delete.tooltip.label'));
    return fullTooltips.join('\n');
  };
  const setItemDeletableKeyboardTooltipRef = useKeyboardTooltip({
    getTooltip
  });
  const onListItemSelected = useCallback(() => {
    if (item.subSections || props.disabled) return;
    onSelected([[sectionIndex, index]], item.key);
  }, [onSelected, sectionIndex, index, item, props.disabled]);
  const onSubItemSelected = useCallback((subItemLocation, key) => {
    // Select a chain of items consisting of this item followed by
    // the items that were selected in the sub-menu
    onSelected([[sectionIndex, index], ...subItemLocation], key);
  }, [onSelected, sectionIndex, index]);
  const onMenuItemClosed = useCallback(() => {
    if (item.subSections || props.disabled || !item.closable || !onClosed) return;
    onClosed([[sectionIndex, index]]);
  }, [onClosed, sectionIndex, index, item, props.disabled]);
  const onSubItemClosed = useCallback(subItemLocation => {
    if (!onClosed) return;
    onClosed([[sectionIndex, index], ...subItemLocation]);
  }, [onClosed, sectionIndex, index]);
  const onHover = useCallback(() => {
    // Focus this list item
    onItemHovered?.([[sectionIndex, index]]);
  }, [onItemHovered, sectionIndex, index]);
  const onSubItemHovered = useCallback(subItemLocation => {
    // Focus a chain of items consisting of this item followed by
    // the items that were focused in the sub-menu
    onItemHovered?.([[sectionIndex, index], ...subItemLocation]);
  }, [onItemHovered, sectionIndex, index]);
  const onLeave = useCallback(() => {
    onItemLeaved?.([[sectionIndex, index]]);
  }, [onItemLeaved, sectionIndex, index]);
  const onSubItemleaved = useCallback(subItemLocation => {
    onItemLeaved?.([[sectionIndex, index], ...subItemLocation]);
  }, [onItemLeaved, sectionIndex, index]);
  const [current, ...rest] = props.focusLocation || [];
  const isInFocusChain = !!current && current[0] === sectionIndex && current[1] === index;
  // This item is focused if it is the only item in the focus chain
  const isFocused = isInFocusChain && props.focusLocation.length === 1;
  const focused = isFocused && showFocus;
  useEffect(() => {
    if (focused) {
      saveTextSelection();
      liRef.current?.focus();
      restoreTextSelection();
    }
  }, [focused]);

  // If navigating with the keyboard, the menu should be open if one of its items gets focus
  // If navigating with the mouse, the menu should be open if this item is hovered.
  const subSections = item.subSections;
  let hasSubMenu = !!subSections && !!subSections.length;
  if (subSections && hasSubMenu) {
    hasSubMenu = hasSubMenu && subSections.some(subSection => subSection.title || subSection.items.length > 0);
  }
  const isSubMenuOpen = showFocus ? hasSubMenu && isInFocusChain && !isFocused : hasSubMenu && isInFocusChain;
  const closable = !item.subSections && item.closable;
  const selected = isInFocusChain && (showFocus || isSubMenuOpen);
  const indentationLevel = item.indentation ?? 0;
  const additionalAriaText = props.additionalAriaText ?? item.tooltip;
  const indentationMarkerText = localize('item.level.aria', {
    num: (indentationLevel + 1).toString()
  });
  const listAriaLabelledBy = [];
  if (item.icon && 'icon' in item.icon) listAriaLabelledBy.push(customIconMarkerId);
  listAriaLabelledBy.push(textMarkerId);
  if (item.info?.infoText) listAriaLabelledBy.push(infoTextId);
  if (additionalAriaText) listAriaLabelledBy.push(additionalAriaTextMarkerId);
  if (props.menuHasIndentation) listAriaLabelledBy.push(indentationMarkerId);
  const playVisualEffects = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  const tokens = getMenuItemDesignTokens(dt, {
    overrides: props.tokens,
    indentationLevel,
    playVisualEffects
  });

  // FIXME: currently, the 'menuitem' role isn't added to the menuitem, since it causes the group cannot be read by JAWS.
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      ref: itemRef,
      role: "presentation",
      className: cn.itemContainer,
      onMouseEnter: !props.disabled ? onHover : undefined,
      onMouseLeave: !props.disabled ? onLeave : undefined,
      children: /*#__PURE__*/_jsx("li", {
        id: props.id,
        "aria-labelledby": listAriaLabelledBy.join(' '),
        "aria-describedby": closable ? deleteInfoMarkerId : undefined,
        title: item.tooltip,
        ref: element => {
          setRef(liRef, element);
          setRef(forwardedRef, element);
          if (focused) setItemDeletableKeyboardTooltipRef(element);
        },
        tabIndex: isFocused ? 0 : -1,
        onBlur: () => {
          isKeyDownRef.current = false;
        },
        onKeyDown: event => {
          isKeyDownRef.current = true;
          if ((event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowRight') && item.subSections?.length && props.disabled) {
            event.preventDefault();
            event.stopPropagation();
            earcon.play();
          }
        },
        onKeyUp: event => {
          // ignore if keydown was not on this element
          if (!isKeyDownRef.current) return;
          isKeyDownRef.current = false;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (item.convertKeyToMouse) {
              liRef.current?.click();
            } else {
              onListItemSelected();
            }
            if (!item.subSections?.length && props.disabled) earcon.play();
          } else if (event.key === 'Delete' || getOSName() === OSName.Mac && event.key === 'Backspace') {
            event.preventDefault();
            onMenuItemClosed();
            if (item.subSections || props.disabled || !item.closable || !onClosed) earcon.play();
          }
        },
        onClick: event => {
          // HACK: Button calls preventDefault on onClick events
          if (!event.defaultPrevented) {
            restoreTextSelection();
            onListItemSelected();
            event.preventDefault();
          }
        },
        onMouseDown: () => saveTextSelection(),
        style: tokens,
        className: cx(cn.item, (hasSubMenu || closable) && cn.operationItem,
        /**
         * There are several cases about the item background color(BGC) with submenu, and they are consistent with HC menu.
         *
         * 1) When hovering the item directly, its BGC is 'hoverable' style.
         * 2) If hovering the submenu opened by 1), the above item BGC is in 'selected' style.
         * 3) When leaving the item and the entire menu in 1), the item BGC is 'selected' style.
         * 4) If leaving the submenu in 2) and the entire menu, the item BGC is still 'selected' style.
         * 5) When hovering the parent item of the submenu in 2), the item BGC becomes 'hoverable' style.
         * 6) If opening the 3rd level submenu and hovering on it, the 1st parent item and the 2nd parent item are both 'selected' style.
         */
        selected && !props.disabled && cn.selected, focused && cn.focused, !item.icon && props.needStartIconSpacing && cn.hasIconSpacing, !hasSubMenu && !closable && props.hasMenuItemRightIcon && cn.hasRightIcon, props.disabled && cn.disabled),
        "data-testid": item.testId,
        "aria-checked": item.checked,
        "aria-disabled": props.disabled ? true : undefined,
        "aria-haspopup": hasSubMenu ? true : undefined,
        role: item.checked === undefined ? 'menuitem' : props.isRadio ? 'menuitemradio' : 'menuitemcheckbox',
        children: /*#__PURE__*/_jsxs("div", {
          className: cn.itemInner,
          children: [item.checked && /*#__PURE__*/_jsx(Icon, {
            icon: MenuCheck,
            className: cx(cn.checkmark, cn.itemIcon),
            disabled: props.disabled
          }), item.icon ? /*#__PURE__*/_jsx(Icon, {
            id: customIconMarkerId,
            className: cn.itemIcon,
            ...('icon' in item.icon ? item.icon : {
              tooltip: '',
              ariaLabel: '',
              icon: item.icon,
              ariaHidden: true
            }),
            disabled: props.disabled
          }) : null, /*#__PURE__*/_jsxs("div", {
            className: cx(cn.textContainer, item.info?.infoHasPriority && cn.infoHasPriority, item.info?.infoText && cn.hasSecondaryText),
            children: [/*#__PURE__*/_jsx("span", {
              id: textMarkerId,
              ref: ref => {
                truncationMainTextRef(ref);
                truncationMainTextHtmlRef.current = ref;
              },
              className: cx(cn.itemText, props.disabled && cn.itemDisabled),
              "data-testid": postfixTestId(props.testId, 'text'),
              children: item.text
            }), item.info?.infoText ? /*#__PURE__*/_jsx("span", {
              id: infoTextId,
              ref: ref => {
                truncationSecondaryTextRef(ref);
                truncationSecondaryTextHtmlRef.current = ref;
              },
              className: cx(cn.itemSecondaryText, props.disabled && cn.itemDisabled),
              children: item.info?.infoText
            }) : null]
          }), props.menuHasIndentation ? /*#__PURE__*/_jsx(InvisibleText, {
            id: indentationMarkerId,
            children: indentationMarkerText
          }) : null, additionalAriaText ? /*#__PURE__*/_jsx(InvisibleText, {
            id: additionalAriaTextMarkerId,
            children: additionalAriaText
          }) : null, closable ? /*#__PURE__*/_jsx(InvisibleText, {
            id: deleteInfoMarkerId,
            children: item.closableAriaText ?? localize('item.delete.aria')
          }) : null, hasSubMenu && /*#__PURE__*/_jsx(Icon, {
            className: cn.navigationIcon,
            icon: DisclosureNext,
            disabled: props.disabled
          }), closable ? /*#__PURE__*/_jsx("span", {
            className: cx(cn.closeButton, props.disabled && cn.closeButtonDisabled),
            children: /*#__PURE__*/_jsx(Icon, {
              testId: postfixTestId(props.testId, 'closeButton'),
              tooltip: item.closeIconTooltip ?? localize('item.close.icon.tooltip.label'),
              ariaHidden: true,
              icon: CloseTab,
              className: cn.closeButtonIcon,
              tabIndex: -1,
              onClick: e => {
                onMenuItemClosed();
                e.stopPropagation();
              },
              disabled: props.disabled
            })
          }) : null]
        })
      })
    }), isSubMenuOpen ? /*#__PURE__*/_jsx(MenuPopover, {
      testId: postfixTestId(props.testId, 'menu'),
      maxHeight: item.submenuMaxHeight,
      onKeyDown: e => {
        if (e.key === 'Escape') {
          props.onEscape(e);
          e.stopPropagation();
        }
        if (e.key === ' ') {
          props.onSpace(e);
          e.stopPropagation();
        }
      },
      isOpen: isSubMenuOpen,
      isSubMenu: true,
      disabled: props.disabled || item.disabled,
      sections: item.subSections,
      referenceElementRef: itemRef,
      placement: `${isRTL ? 'left' : 'right'}-start`,
      focusLocation: rest || null,
      showFocus: showFocus,
      onItemHovered: onSubItemHovered,
      onItemLeaved: onSubItemleaved,
      onItemSelected: onSubItemSelected,
      onItemClosed: onSubItemClosed,
      onSubmenuClosed: props.onSubmenuClosed,
      menuLevel: props.menuLevel ? props.menuLevel + 1 : 1,
      popoverTheme: props.popoverTheme,
      listTheme: props.listTheme,
      theme: props.menuTheme
    }) : null]
  });
});
export const getMenuItemDesignTokens = (dt, opts) => {
  return {
    ...renderDesignTokens({
      'processing-anim-duration': dt.tokens['duration-700'],
      'processing-anim-iteration-count': opts?.playVisualEffects ? 'infinite' : 0,
      'indentation-level': opts?.indentationLevel,
      'font-family': opts?.overrides?.().fontFamily ?? 'inherit',
      fg: opts?.overrides?.().fg ?? undefined
    }, 'menu-list-item'),
    ...(!dt.enableNovaTokens && getMenuItemLegacyDesignTokens(opts))
  };
};
export const getMenuItemLegacyDesignTokens = opts => {
  return renderDesignTokens({
    'indentation-level': opts?.indentationLevel,
    'font-family': opts?.overrides?.().fontFamily ?? 'inherit',
    fg: opts?.overrides?.().fg ?? undefined
  }, 'menu-list-item');
};
//# sourceMappingURL=MenuListItem.js.map